import React, { useState } from 'react';
import { X, Star, Send, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const FeedbackWidget = ({ user }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const feedbackCategories = [
    { value: '', label: t('feedback.selectType') },
    { value: 'general', label: t('feedback.general') },
    { value: 'bug', label: t('feedback.bug') },
    { value: 'feature', label: t('feedback.feature') },
    { value: 'content', label: t('feedback.content') },
    { value: 'usability', label: t('feedback.usability') },
    { value: 'performance', label: t('feedback.performance') },
    { value: 'design', label: t('feedback.design') },
    { value: 'documentation', label: t('feedback.documentation') },
    { value: 'other', label: t('feedback.other') },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!feedbackType) {
      toast.error('Please select a feedback type');
      return;
    }
    
    if (!message.trim()) {
      toast.error('Please enter your feedback');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: feedbackType,
          rating: rating,
          message: message.trim(),
          user_email: user?.email,
          user_name: user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : null,
          page_url: window.location.href
        })
      });

      if (response.ok) {
        toast.success(t('feedback.successMessage'));
        setIsOpen(false);
        setFeedbackType('');
        setRating(0);
        setMessage('');
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to submit feedback');
      }
    } catch (error) {
      toast.error(t('feedback.errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Feedback Tab - Fixed to right edge */}
      <button
        onClick={() => setIsOpen(true)}
        data-testid="feedback-tab"
        style={{
          position: 'fixed',
          right: 0,
          bottom: '24px',
          backgroundColor: '#1B5E5F',
          color: 'white',
          border: 'none',
          padding: '16px 10px',
          fontSize: '14px',
          fontWeight: '600',
          letterSpacing: '0.5px',
          cursor: 'pointer',
          borderRadius: '8px 0 0 8px',
          boxShadow: '-2px 0 10px rgba(0,0,0,0.15)',
          zIndex: 999,
          display: isOpen ? 'none' : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#0C6A1D';
          e.currentTarget.style.paddingLeft = '14px';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#1B5E5F';
          e.currentTarget.style.paddingLeft = '10px';
        }}
      >
        {t('feedback.title')}
      </button>

      {/* Slide-out Panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: isOpen ? 0 : '-400px',
          width: '380px',
          height: '100vh',
          backgroundColor: 'white',
          boxShadow: isOpen ? '-4px 0 20px rgba(0,0,0,0.15)' : 'none',
          zIndex: 1001,
          transition: 'right 0.3s ease-in-out',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        data-testid="feedback-panel"
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          backgroundColor: '#1B5E5F',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#FFFFFF' }}>
              {t('feedback.title')}
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'rgba(255,255,255,0.9)' }}>
              {t('feedback.subtitle')}
            </p>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '6px',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            data-testid="feedback-close-btn"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ 
          padding: '24px', 
          flex: 1, 
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }}>
          {/* Feedback Type Dropdown */}
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#374151', 
              marginBottom: '8px' 
            }}>
              {t('feedback.feedbackType')} *
            </label>
            <div style={{ position: 'relative' }}>
              <select
                value={feedbackType}
                onChange={(e) => setFeedbackType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 40px 12px 14px',
                  borderRadius: '8px',
                  border: '1px solid #D1D5DB',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  appearance: 'none',
                  color: feedbackType ? '#1F2937' : '#9CA3AF',
                }}
                data-testid="feedback-type-select"
              >
                {feedbackCategories.map((cat) => (
                  <option key={cat.value} value={cat.value} disabled={cat.value === ''}>
                    {cat.label}
                  </option>
                ))}
              </select>
              <ChevronDown 
                size={18} 
                style={{ 
                  position: 'absolute', 
                  right: '14px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: '#6B7280',
                  pointerEvents: 'none'
                }} 
              />
            </div>
          </div>

          {/* Star Rating */}
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#374151', 
              marginBottom: '8px' 
            }}>
              {t('feedback.rateExperience')} <span style={{ fontWeight: '400', color: '#9CA3AF' }}>({t('common.optional')})</span>
            </label>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    transition: 'transform 0.15s ease',
                    transform: (hoverRating || rating) >= star ? 'scale(1.15)' : 'scale(1)',
                  }}
                  data-testid={`feedback-star-${star}`}
                >
                  <Star
                    size={28}
                    fill={(hoverRating || rating) >= star ? '#F59E0B' : 'none'}
                    stroke={(hoverRating || rating) >= star ? '#F59E0B' : '#D1D5DB'}
                    strokeWidth={1.5}
                  />
                </button>
              ))}
              {rating > 0 && (
                <span style={{ marginLeft: '8px', fontSize: '13px', color: '#6B7280' }}>
                  {rating === 1 ? t('feedback.ratingLabels.1') : rating === 2 ? t('feedback.ratingLabels.2') : rating === 3 ? t('feedback.ratingLabels.3') : rating === 4 ? t('feedback.ratingLabels.4') : t('feedback.ratingLabels.5')}
                </span>
              )}
            </div>
          </div>

          {/* Message */}
          <div style={{ flex: 1 }}>
            <label style={{ 
              display: 'block', 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#374151', 
              marginBottom: '8px' 
            }}>
              {t('feedback.yourFeedback')} *
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                feedbackType === 'bug' 
                  ? t('feedback.placeholderBug')
                  : feedbackType === 'feature'
                  ? t('feedback.placeholderFeature')
                  : feedbackType === 'content'
                  ? t('feedback.placeholderContent')
                  : t('feedback.placeholder')
              }
              style={{
                width: '100%',
                minHeight: '140px',
                padding: '12px 14px',
                borderRadius: '8px',
                border: '1px solid #D1D5DB',
                fontSize: '14px',
                resize: 'vertical',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                lineHeight: '1.5',
              }}
              data-testid="feedback-message"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || !message.trim() || !feedbackType}
            style={{
              width: '100%',
              padding: '14px 24px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: (isSubmitting || !message.trim() || !feedbackType) ? '#D1D5DB' : '#0C6A1D',
              color: 'white',
              fontSize: '15px',
              fontWeight: '600',
              cursor: (isSubmitting || !message.trim() || !feedbackType) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s ease',
            }}
            data-testid="feedback-submit-btn"
          >
            <Send size={18} />
            {isSubmitting ? t('feedback.submitting') : t('feedback.submit')}
          </button>

          {/* User info */}
          {user?.email && (
            <p style={{
              fontSize: '12px',
              color: '#9CA3AF',
              textAlign: 'center',
              margin: 0,
            }}>
              {t('feedback.submitAs', { email: user.email })}
            </p>
          )}
        </form>
      </div>

      {/* Backdrop */}
      {isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            zIndex: 1000,
          }}
        />
      )}
    </>
  );
};

export default FeedbackWidget;
